import { Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon } from "@mui/icons-material";
import {
	Alert,
	Button,
	Card,
	Container,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	IconButton,
	Paper,
	Snackbar,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TextField,
	Typography,
} from "@mui/material";
import { useState } from "react";
import { Helmet } from "react-helmet-async";
import type { VideoCategory } from "src/components/videos/types";
import { useVideoCategories } from "src/hooks/useVideoCategories";

export default function VideoCategoriesPage() {
	const { categories, isLoading, createCategory, updateCategory, deleteCategoryAsync, isCreating, isUpdating, isDeleting } =
		useVideoCategories();

	const [editDialog, setEditDialog] = useState<{
		open: boolean;
		category: VideoCategory | null;
		isNew: boolean;
	}>({
		open: false,
		category: null,
		isNew: false,
	});

	const [deleteDialog, setDeleteDialog] = useState<{
		open: boolean;
		category: VideoCategory | null;
	}>({
		open: false,
		category: null,
	});

	const [snackbar, setSnackbar] = useState({
		open: false,
		message: "",
		severity: "success" as "success" | "error",
	});

	const [categoryName, setCategoryName] = useState("");

	const showSnackbar = (message: string, severity: "success" | "error") => {
		setSnackbar({ open: true, message, severity });
	};

	const handleAddCategory = () => {
		setCategoryName("");
		setEditDialog({
			open: true,
			category: null,
			isNew: true,
		});
	};

	const handleEditCategory = (category: VideoCategory) => {
		setCategoryName(category.category_name);
		setEditDialog({
			open: true,
			category,
			isNew: false,
		});
	};

	const handleDeleteCategory = (category: VideoCategory) => {
		setDeleteDialog({
			open: true,
			category,
		});
	};

	const handleSaveCategory = () => {
		if (!categoryName.trim()) {
			showSnackbar("Category name is required", "error");
			return;
		}

		try {
			if (editDialog.isNew) {
				createCategory(categoryName.trim());
				showSnackbar("Category created successfully", "success");
			} else if (editDialog.category) {
				updateCategory({
					categoryId: editDialog.category.video_category_id,
					name: categoryName.trim(),
				});
				showSnackbar("Category updated successfully", "success");
			}
			handleCloseEditDialog();
		} catch (error) {
			console.error("Error saving category:", error);
			showSnackbar("Error saving category", "error");
		}
	};

	const handleConfirmDelete = async () => {
		if (!deleteDialog.category) return;
		try {
			await deleteCategoryAsync(deleteDialog.category.video_category_id);
			showSnackbar("Category deleted successfully", "success");
			setDeleteDialog({ open: false, category: null });
		} catch (error) {
			console.error("Error deleting category:", error);
			const err = error as { status?: number; message?: string; data?: { message?: string } };
			if (err?.status === 409) {
				showSnackbar(err.data?.message || err.message || "This category is in use and cannot be deleted", "error");
			} else {
				showSnackbar(err?.message || "Error deleting category", "error");
			}
		}
	};

	const handleCloseEditDialog = () => {
		setEditDialog({ open: false, category: null, isNew: false });
		setCategoryName("");
	};

	const handleCloseDeleteDialog = () => {
		setDeleteDialog({ open: false, category: null });
	};

	if (isLoading) {
		return (
			<Container>
				<Typography>Loading categories...</Typography>
			</Container>
		);
	}

	return (
		<>
			<Helmet>
				<title>Video Categories</title>
			</Helmet>

			<Container>
				<Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
					<Stack>
						<Typography variant="h4" gutterBottom>
							Video Categories
						</Typography>
						<Typography variant="body2" color="textSecondary">
							Manage video categories: {categories.length} total
						</Typography>
					</Stack>
					<Button variant="contained" startIcon={<AddIcon />} onClick={handleAddCategory} disabled={isCreating}>
						Add Category
					</Button>
				</Stack>

				<Card>
					<TableContainer component={Paper}>
						<Table>
							<TableHead>
								<TableRow>
									<TableCell>ID</TableCell>
									<TableCell>Name</TableCell>
									<TableCell align="right">Actions</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{categories.length > 0 ? (
									categories.map((category) => (
										<TableRow key={category.video_category_id}>
											<TableCell>{category.video_category_id}</TableCell>
											<TableCell>
												<Typography variant="body2" fontWeight={500}>
													{category.category_name}
												</Typography>
											</TableCell>
											<TableCell align="right">
												<Stack direction="row" spacing={1} justifyContent="flex-end">
													<IconButton size="small" onClick={() => handleEditCategory(category)} disabled={isUpdating}>
														<EditIcon fontSize="small" />
													</IconButton>
													<IconButton size="small" color="error" onClick={() => handleDeleteCategory(category)} disabled={isDeleting}>
														<DeleteIcon fontSize="small" />
													</IconButton>
												</Stack>
											</TableCell>
										</TableRow>
									))
								) : (
									<TableRow>
										<TableCell colSpan={3} align="center" sx={{ py: 6 }}>
											<Typography variant="h6" color="textSecondary" gutterBottom>
												📂 No categories found
											</Typography>
											<Typography variant="body2" color="textSecondary" mb={3}>
												Create your first category to organize videos
											</Typography>
											<Button variant="contained" startIcon={<AddIcon />} onClick={handleAddCategory}>
												Add First Category
											</Button>
										</TableCell>
									</TableRow>
								)}
							</TableBody>
						</Table>
					</TableContainer>
				</Card>

				{/* Edit/Add Category Dialog */}
				<Dialog open={editDialog.open} onClose={handleCloseEditDialog} maxWidth="sm" fullWidth>
					<DialogTitle>{editDialog.isNew ? "Add Category" : "Edit Category"}</DialogTitle>
					<DialogContent>
						<TextField
							autoFocus
							label="Category Name"
							fullWidth
							variant="outlined"
							value={categoryName}
							onChange={(e) => setCategoryName(e.target.value)}
							margin="normal"
							placeholder="Enter category name"
							helperText="Category name should be descriptive and unique"
						/>
					</DialogContent>
					<DialogActions>
						<Button onClick={handleCloseEditDialog}>Cancel</Button>
						<Button onClick={handleSaveCategory} variant="contained" disabled={!categoryName.trim() || isCreating || isUpdating}>
							{editDialog.isNew ? "Create" : "Update"}
						</Button>
					</DialogActions>
				</Dialog>

				{/* Delete Confirmation Dialog */}
				<Dialog open={deleteDialog.open} onClose={handleCloseDeleteDialog} maxWidth="sm">
					<DialogTitle>Delete Category</DialogTitle>
					<DialogContent>
						<Typography>
							Are you sure you want to delete the category "<strong>{deleteDialog.category?.category_name}</strong>"?
						</Typography>
						<Typography variant="body2" color="error" sx={{ mt: 2 }}>
							Categories currently assigned to videos cannot be deleted.
						</Typography>
					</DialogContent>
					<DialogActions>
						<Button onClick={handleCloseDeleteDialog}>Cancel</Button>
						<Button onClick={handleConfirmDelete} color="error" variant="contained" disabled={isDeleting}>
							Delete
						</Button>
					</DialogActions>
				</Dialog>

				{/* Snackbar для повідомлень */}
				<Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
					<Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: "100%" }}>
						{snackbar.message}
					</Alert>
				</Snackbar>
			</Container>
		</>
	);
}
